import { NewParagraph, ParagraphEdit } from "@/domains/pusher/schemas";
import { SpaceAccountNoteSuggestion } from "@/store/note";
import {
  SessionUpsertedSyncUpdateValue,
  SessionEventUpsertedSyncUpdateValue,
  SessionStatus,
} from "@/store/sessions/types";
import type { SearchEngineResult } from "@/modules/url-params/search-engine-params/types";

export enum PusherServicePrefix {
  BACKEND_API = "BACKEND_API",
  AUTHENTICATED_BACKEND_API = "private-BACKEND_API",
}

export enum PusherDomainPrefix {
  IMAGE_FILE = "IMAGE_FILE",
  SPACE_SCOPED_SYNC_UPDATE = "SPACE_SCOPED_SYNC_UPDATE",
  COLLECTION_SCOPED_SYNC_UPDATE = "COLLECTION_SCOPED_SYNC_UPDATE",
  SPACE_ACCOUNT_SCOPED_SYNC_UPDATE = "SPACE_ACCOUNT_SCOPED_SYNC_UPDATE",
  SPACE_ACCOUNT_NOTE_LIVE_SYNC_UPDATE = "SPACE_ACCOUNT_NOTE_LIVE_SYNC_UPDATE",
  SESSION_LIVE_SYNC_UPDATE = "SESSION_LIVE_SYNC_UPDATE",
  AGENTIC_SEARCH = "AGENTIC_SEARCH",
  PLATFORM_INFO = "PLATFORM_INFO",
}

export enum PusherEventKind {
  BACKEND_IMAGE_FILE_INDEXING_STATUS_UPDATE = "INDEXING_STATUS_UPDATE",
  SYNC_UPDATE_PUBLISHED = "SYNC_UPDATE_PUBLISHED",
  SPACE_ACCOUNT_NOTE_SUGGESTION_PUBLISHED = "SPACE_ACCOUNT_NOTE_SUGGESTION_PUBLISHED",
  ASSISTANT_NOTE_EDIT_SUGGESTED = "ASSISTANT_NOTE_EDIT_SUGGESTED",
  SESSION_UPSERTED = "SESSION_UPSERTED",
  SESSION_EVENT_UPSERTED = "SESSION_EVENT_UPSERTED",
  SESSION_STATUS_UPDATED = "SESSION_STATUS_UPDATED",
  AGENTIC_SEARCH_PATCH = "AGENTIC_SEARCH_PATCH",
  PLATFORM_INFO_INVALIDATED = "PLATFORM_INFO_INVALIDATED",
}

export type AgenticSearchResultTarget = {
  result_id?: string | null;
  note_id?: string | null;
  collection_id?: string | null;
};

export type AgenticSearchResultGroup = {
  id: string;
  label: string;
  resultIds: string[];
};

export type AgenticSearchResultUpdate = AgenticSearchResultTarget & {
  append?: boolean;
  hint?: string | null;
};

type AgenticSearchPatchBase = {
  agentic_search_session_id: string;
  sequence: number;
};

export type AgenticSearchPatchEventData =
  | (AgenticSearchPatchBase & {
      kind: "status";
      payload: { message: string };
    })
  | (AgenticSearchPatchBase & {
      kind: "update_results";
      payload: {
        status_message?: string | null;
        results?: SearchEngineResult[];
        updates?: AgenticSearchResultUpdate[];
      };
    })
  | (AgenticSearchPatchBase & {
      kind: "group_results";
      payload: { groups: AgenticSearchResultGroup[] };
    })
  | (AgenticSearchPatchBase & {
      kind: "reorder_results";
      payload: { resultIds: string[] };
    })
  | (AgenticSearchPatchBase & {
      kind: "done";
      payload: Record<string, never>;
    })
  | (AgenticSearchPatchBase & {
      kind: "failed";
      payload: { message: string };
    });

export type PusherEventData<TKind extends PusherEventKind> = {
  [PusherEventKind.BACKEND_IMAGE_FILE_INDEXING_STATUS_UPDATE]: {
    image_file_id: string;
    processing_status: string;
    indexing_status: string;
    suggested_search: string | null;
  };
  [PusherEventKind.SYNC_UPDATE_PUBLISHED]: {
    id: string;
  };
  [PusherEventKind.SPACE_ACCOUNT_NOTE_SUGGESTION_PUBLISHED]: {
    note_id: string;
    space_account_id: string;
    space_account_note_id: string;
    suggestions: SpaceAccountNoteSuggestion[];
  };
  [PusherEventKind.ASSISTANT_NOTE_EDIT_SUGGESTED]: {
    paragraph_edits: ParagraphEdit[];
    new_paragraphs: NewParagraph[];
  };
  [PusherEventKind.SESSION_UPSERTED]: {
    sync_operation_id: string;
    value: SessionUpsertedSyncUpdateValue;
  };
  [PusherEventKind.SESSION_EVENT_UPSERTED]: {
    sync_operation_id: string;
    value: SessionEventUpsertedSyncUpdateValue;
  };
  [PusherEventKind.SESSION_STATUS_UPDATED]: {
    session_id: string;
    status: SessionStatus;
  };
  [PusherEventKind.AGENTIC_SEARCH_PATCH]: AgenticSearchPatchEventData;
  [PusherEventKind.PLATFORM_INFO_INVALIDATED]: {
    space_account_id: string;
  };
}[TKind];
